<template>
  <div class="button-bar">
    <div class="button-bar__inner button-bar__inner--top">
      <button class="button-bar__button button-bar__button--navigation-bar-toggler button" :class="{ 'button-bar__button--on': layoutSettings.showNavigationBar }" v-if="!light" @click="toggleNavigationBar()" v-title="'Toggle navigation bar'">
        <icon-navigation-bar></icon-navigation-bar>
      </button>
      <button class="button-bar__button button-bar__button--side-preview-toggler button" :class="{ 'button-bar__button--on': layoutSettings.showSidePreview }" tour-step-anchor="editor" @click="toggleSidePreview()" v-title="'Toggle side preview'">
        <icon-side-preview></icon-side-preview>
      </button>
      <button class="button-bar__button button-bar__button--editor-toggler button" @click="toggleEditor(false)" v-title="'Reader mode'">
        <icon-eye></icon-eye>
      </button>
    </div>
    <div class="button-bar__inner button-bar__inner--bottom">
      <button class="button-bar__button button-bar__button--focus-mode-toggler button" :class="{ 'button-bar__button--on': layoutSettings.focusMode }" @click="toggleFocusMode()" v-title="'Toggle focus mode'">
        <icon-target></icon-target>
      </button>
      <button class="button-bar__button button-bar__button--scroll-sync-toggler button" :class="{ 'button-bar__button--on': layoutSettings.scrollSync }" @click="toggleScrollSync()" v-title="'Toggle scroll sync'">
        <icon-scroll-sync></icon-scroll-sync>
      </button>
      <button class="button-bar__button button-bar__button--status-bar-toggler button" :class="{ 'button-bar__button--on': layoutSettings.showStatusBar }" @click="toggleStatusBar()" v-title="'Toggle status bar'">
        <icon-status-bar></icon-status-bar>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  computed: {
    ...mapState([
      'light',
    ]),
    ...mapGetters('data', [
      'layoutSettings',
    ]),
  },
  methods: mapActions('data', [
    'toggleNavigationBar',
    'toggleEditor',
    'toggleSidePreview',
    'toggleStatusBar',
    'toggleFocusMode',
    'toggleScrollSync',
  ]),
};
</script>

<style lang="scss">
@import '../styles/variables.scss';

.button-bar {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: #f3f3f3;
  padding: 0;
  margin: 0;

  .app--light & {
    background-color: #e8e8e8;
  }

  .app--dark & {
    background-color: #252525;
  }

  .app--sepia & {
    background-color: #e8dcc8;
  }

  .app--nord & {
    background-color: #3b4252;
  }

  .app--solarized-light & {
    background-color: #eee8d5;
  }

  .app--solarized-dark & {
    background-color: #073642;
  }

  .app--dracula & {
    background-color: #21222c;
  }

  .app--monokai & {
    background-color: #1e1f1c;
  }
}

.button-bar__inner {
  position: absolute;
  left: 0;
  right: 0;
  width: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  top: 0;
}

.button-bar__inner--bottom {
  top: auto;
  bottom: 0;
}

.button-bar__button {
  color: rgba(0, 0, 0, 0.5);
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 26px;
  padding: 0 !important;
  margin: 3px auto;
  box-sizing: border-box;

  .icon {
    width: 20px;
    height: 20px;
  }

  .app--light & {
    color: rgba(0, 0, 0, 0.5);
  }

  .app--dark & {
    color: rgba(255, 255, 255, 0.5);
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.6);
  }

  .app--nord & {
    color: rgba(216, 222, 233, 0.6);
  }

  .app--solarized-light & {
    color: rgba(101, 123, 131, 0.7);
  }

  .app--solarized-dark & {
    color: rgba(147, 161, 161, 0.6);
  }

  .app--dracula & {
    color: rgba(248, 248, 242, 0.6);
  }

  .app--monokai & {
    color: rgba(248, 248, 242, 0.6);
  }

  &:active,
  &:focus,
  &:hover {
    color: rgba(0, 0, 0, 0.7);

    .app--light & {
      color: rgba(0, 0, 0, 0.7);
    }

    .app--dark & {
      color: rgba(255, 255, 255, 0.7);
      background-color: $navbar-hover-background;
    }

    .app--sepia & {
      color: rgba(91, 70, 54, 0.8);
    }

    .app--nord & {
      color: rgba(216, 222, 233, 0.8);
    }

    .app--solarized-light & {
      color: rgba(101, 123, 131, 0.9);
    }

    .app--solarized-dark & {
      color: rgba(147, 161, 161, 0.8);
    }

    .app--dracula & {
      color: rgba(248, 248, 242, 0.8);
    }

    .app--monokai & {
      color: rgba(248, 248, 242, 0.8);
    }
  }
}

.button-bar__button--on {
  color: rgba(0, 0, 0, 0.8);

  .app--light & {
    color: rgba(0, 0, 0, 0.8);
  }

  .app--dark & {
    color: rgba(255, 255, 255, 0.8);
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.9);
  }

  .app--nord & {
    color: rgba(216, 222, 233, 0.9);
  }

  .app--solarized-light & {
    color: rgba(101, 123, 131, 1);
  }

  .app--solarized-dark & {
    color: rgba(147, 161, 161, 0.9);
  }

  .app--dracula & {
    color: rgba(248, 248, 242, 0.9);
  }

  .app--monokai & {
    color: rgba(248, 248, 242, 0.9);
  }

  &:active,
  &:focus,
  &:hover {
    color: rgba(0, 0, 0, 0.9);

    .app--light & {
      color: rgba(0, 0, 0, 0.9);
    }

    .app--dark & {
      color: rgba(255, 255, 255, 0.9);
    }

    .app--sepia & {
      color: rgba(91, 70, 54, 1);
    }

    .app--nord & {
      color: rgba(216, 222, 233, 1);
    }

    .app--solarized-light & {
      color: rgba(101, 123, 131, 1);
    }

    .app--solarized-dark & {
      color: rgba(147, 161, 161, 1);
    }

    .app--dracula & {
      color: rgba(248, 248, 242, 1);
    }

    .app--monokai & {
      color: rgba(248, 248, 242, 1);
    }
  }
}

// Hide side-by-side and scroll sync on mobile (meaningless on small screens)
@media (max-width: 870px) {
  .button-bar__button--side-preview-toggler,
  .button-bar__button--scroll-sync-toggler {
    display: none !important;
  }
}

</style>
